import { ApiResult, apiUrl } from '../../../api';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';
import {
  PromanReportCollection,
  PromanReport,
  PromanDocument,
  PromanProjectWithFolders,
} from './promanDtos';

// Get all report collections for a Proman project
export const apiGetPromanReportCollections = async (
  promanProjectId: number
): Promise<ApiResult<PromanReportCollection[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/proman/promanProjects/${promanProjectId}/reportCollections`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Get all reports for a specific report collection
export const apiGetPromanReports = async (
  promanProjectId: number,
  reportCollectionID: number
): Promise<ApiResult<PromanReport[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/proman/promanProjects/${promanProjectId}/reportCollections/${reportCollectionID}/reports`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Get all documents for a Proman project
export const apiGetPromanDocuments = async (
  promanProjectId: number
): Promise<ApiResult<PromanDocument[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/proman/promanProjects/${promanProjectId}/documents?PrintDocumentType=All`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};
export const apiDownloadPromanDocument = async (
  documentId: number
): Promise<void> => {
  try {
    const response = await fetchWithRetry(
      `${apiUrl}/api/proman/documents/${documentId}/download`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to download document: ${response.status} ${response.statusText}`
      );
    }

    // Get the blob data from the response
    const blob = new Blob([await response.arrayBuffer()], {
      type: 'application/pdf',
    });

    // Try to get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `document_${documentId}.pdf`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

// Get document blob for PDF viewer
export const apiGetPromanDocumentBlob = async (
  documentId: number
): Promise<Response> => {
  const response = await fetchWithRetry(
    `${apiUrl}/integration/proman/documents/${documentId}/download`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to load PDF: ${response.status} ${response.statusText}`
    );
  }

  return response;
};

export const apiGetPromanMandateId = async (): Promise<ApiResult<string>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/proman/mandateId`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.text() : null;
  return {
    result,
    data,
  };
};

// Get report mail data for email creation
export const apiGetPromanReportMail = async (
  promanProjectId: number,
  reportCollectionId: number,
  reportId: number
): Promise<ApiResult<any>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/proman/promanProjects/${promanProjectId}/reportCollections/${reportCollectionId}/reports/${reportId}`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Get a proman project with folders
export const apiGetPromanProject = async (
  promanProjectId: number
): Promise<ApiResult<PromanProjectWithFolders>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/integration/Proman/promanProjects/${promanProjectId}`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};
